import { Component, Injector } from '@angular/core';
import type { OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';

// Tree-shakeable Chart.js imports - only what's actually used
// This reduces bundle size by ~100KB compared to 'chart.js/auto'
import {
    Chart,
    BarController,
    BarElement,
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

// Register only the components we need
Chart.register(
    BarController, BarElement,
    LineController, LineElement, PointElement,
    LinearScale, CategoryScale,
    Title, Tooltip, Legend
);

import { Constants } from '@shared/models';
import { DashboardService } from '@shared/services';
import { ViewBasePage } from '@shared/pages';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.page.html',
    styleUrls: ['./dashboard.page.scss'],
    standalone: true,
    imports: [ CommonModule, FormsModule, IonicModule ]
})
export class DashboardPage extends ViewBasePage implements OnInit, OnDestroy {
    private campaignsCanvas: any = null;
    private customersCanvas: any = null;
    private ticketsCanvas: any = null;
    private tripsCanvas: any = null;

    constructor(private dashboardService: DashboardService, injector: Injector) {
        super(injector);
        super.setCurrentView(Constants.VIEW_DASHBOARD);
    }
    
    public override async ngOnInit(): Promise<void> {
        super.ngOnInit();
        this.initMoreMenu();

        try {
            await this.uxHelperService.showSpinner();
            // this.testDates();
            const p1 = this.initTicketsChart();
            const p2 = this.initTripsChart();
            const p3 = this.initCustomersChart();
            const p4 = this.initCampaignsChart();
            await Promise.all([p1, p2, p3, p4]);
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            this.uxHelperService.showAlert('Error', errorMessage);
        } finally {
            await this.uxHelperService.hideSpinner();
        }
    }

    public override ngOnDestroy(): void {
        if (this.campaignsCanvas) {
            this.campaignsCanvas.destroy();
        }

        if (this.customersCanvas) {
            this.customersCanvas.destroy();
        }

        if (this.ticketsCanvas) {
            this.ticketsCanvas.destroy();
        }

        if (this.tripsCanvas) {
            this.tripsCanvas.destroy();
        }

        super.ngOnDestroy();
    }

    public initMoreMenu(): void {
        // this.pushPopoverMenuItem('Show Orientation Tour', '', 'Show Orientation Tour');
        this.pushPopoverMenuItem('Show Tour', 'show-dashboard-tour');

        // this.moreMenuItemClick((menuItem: any) => {
        //     switch (menuItem.name) {
        //         case 'Show Orientation Tour':
        //             (<any>window).USETIFUL.checklist.start('01G9MNK3ZSWMKRZJ9NQC0WFFN4');
        //             break;
        //     }
        // });
    }

    private async initTicketsChart(): Promise<void> {
        const chartData: any = await this.dashboardService.getTicketsChartData();
        this.ticketsCanvas = this.buildChart('ticketsCanvas', chartData);
    }

    private async initTripsChart(): Promise<void> {
        const chartData: any = await this.dashboardService.getTripsChartData();
        this.tripsCanvas = this.buildChart('tripsCanvas', chartData);
    }

    private async initCustomersChart(): Promise<void> {
        const chartData: any = await this.dashboardService.getCustomersChartData();
        this.customersCanvas = this.buildChart('customersCanvas', chartData);
    }

    private async initCampaignsChart(): Promise<void> {
        const chartData: any = await this.dashboardService.getCampaignsChartData();
        this.campaignsCanvas = this.buildChart('campaignsCanvas', chartData);
    }

    private buildChart(canvasId: string, chartData: any): Chart {
        if (!chartData) {
            throw new Error('Invalid chart data.');
        }

        const context: HTMLCanvasElement = document.getElementById(
            canvasId
        ) as HTMLCanvasElement;
        const chart = new Chart(context, {
            type: 'bar',
            data: {
                datasets: [
                    {
                        label: 'Daily Values',
                        backgroundColor: chartData.backgroundColors,
                        borderColor: chartData.borderColors,
                        borderWidth: 1,
                        data: chartData.dailyValues,
                        yAxisID: 'y',
                    },
                    {
                        label: 'Running Total Values',
                        data: chartData.runningTotalValues,
                        type: 'line',
                        yAxisID: 'y1',
                    },
                ],
                labels: chartData.labels,
            },
            options: {
                responsive: true,
                plugins: {
                    title: {
                        display: false,
                    },
                    // tooltips: {
                    //     mode: 'index',
                    //     intersect: true
                    // },
                },
                scales: {
                    y: {
                        type: 'linear',
                        display: true,
                        position: 'left',
                    },
                    y1: {
                        type: 'linear',
                        display: true,
                        position: 'right',

                        // grid line settings
                        grid: {
                            drawOnChartArea: false, // only want the grid lines for one axis to show up
                        },
                    },
                },
                // scales: {
                //     xAxes: [{
                //       stacked: true
                //     }],
                //     yAxes: [{
                //       stacked: true,
                //       position: 'left',
                //       type: 'linear',
                //       scaleLabel: {
                //         display: true,
                //       },
                //       id: 'y-axis-1',
                //       ticks: {
                //         beginAtZero:true,
                //         callback: function (tick, index, ticks) {
                //           return numeral(tick).format('(0,0)');
                //         },
                //       }
                //     }, {
                //       stacked: false,
                //       position: 'right',
                //       type: 'linear',
                //       id: 'y-axis-0',
                //       ticks: {
                //         max: 10,
                //         stepSize: 1,
                //         display: true,
                //         beginAtZero: true,
                //         fontSize: 13,
                //         padding: 10,
                //         callback: function (tick, index, ticks) {
                //             return numeral(tick).format('$ 0,0');
                //         }
                //       }
                //     }]
                // scales: {
                //     y: {
                //         beginAtZero: true
                //     }
                // }
                // scales: {
                //     y: [
                //         {
                //             id: 'DailyValues',
                //             display: true,
                //             ticks: {
                //                 beginAtZero: true
                //             },
                //             position: 'left'
                //         },
                //         {
                //             id: 'RunningTotal',
                //             display: true,
                //             ticks: {
                //                 beginAtZero: true
                //             },
                //             position: 'right',
                //             suggestedMax: 100 // point to y-primary suggested max here?
                //         }
                //     ]
                // }
            },
        });
        return chart;
    }
}
